import { useEffect, useRef } from 'react';

/**
 * Hook to scroll a container to the top when page changes. Handles both API
 * calls (waits for loading to complete) and cache hits (scrolls immediately
 * when data updates).
 */
export const useScrollToTopOnPageChange = <T>(
  page: number,
  loading: boolean,
  data: readonly T[],
  tableWrapperRef: React.RefObject<HTMLDivElement>,
) => {
  const previousLoadingRef = useRef<boolean>(loading);
  const waitingForLoadRef = useRef<boolean>(false);

  // When page changes, mark that we're waiting for data to load or update
  useEffect(() => {
    waitingForLoadRef.current = true;
  }, [page]);

  // When loading state changes, track it and scroll when done
  useEffect(() => {
    const wasLoading = previousLoadingRef.current;
    const isLoading = loading;

    // If loading just started, we're now waiting for it to finish
    if (!wasLoading && isLoading) {
      waitingForLoadRef.current = true;
    }

    // If loading just finished and we're waiting, scroll now
    if (
      wasLoading &&
      !isLoading &&
      waitingForLoadRef.current &&
      tableWrapperRef.current
    ) {
      requestAnimationFrame(() => {
        tableWrapperRef.current?.scrollTo({ top: 0 });
      });
      waitingForLoadRef.current = false;
    }

    previousLoadingRef.current = loading;
  }, [loading, tableWrapperRef]);

  // When data updates (cache hit), scroll if we're still waiting
  useEffect(() => {
    if (!loading && waitingForLoadRef.current && tableWrapperRef.current) {
      requestAnimationFrame(() => {
        tableWrapperRef.current?.scrollTo({ top: 0 });
      });
      waitingForLoadRef.current = false;
    }
  }, [data, loading, tableWrapperRef]);
};
